<template>
  <div class="min-h-screen bg-slate-50 px-6 py-5">
    <div class="mx-auto max-w-7xl space-y-4">
      <div class="flex items-center gap-2 text-sm">
        <router-link to="/" class="text-brand-600 hover:text-brand-800">Back to jobs</router-link>
        <span class="text-slate-300">/</span>
        <router-link :to="`/edit/${job.id}`" class="text-brand-600 hover:text-brand-800">Edit</router-link>
      </div>

      <header class="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-950">{{ job.title }}</h1>
        <div class="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
          <p><span class="font-medium text-slate-800">Company:</span> {{ job.company }}</p>
          <p v-if="job.salary_range"><span class="font-medium text-slate-800">Salary:</span> {{ job.salary_range }}</p>
          <p v-if="job.job_url">
            <span class="font-medium text-slate-800">URL:</span>
            <a :href="job.job_url" target="_blank" class="text-brand-600 hover:text-brand-800">{{ domainName }}</a>
          </p>
        </div>
      </header>

      <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <main class="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <h2 class="text-lg font-semibold text-slate-950">Job Description</h2>
          <div
            v-html="processedDescription"
            class="mt-4 max-w-none rounded-md border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700"
          ></div>
          <div class="mt-4">
            <CancelReturn text="Return" @cancel="cancel" />
          </div>
        </main>

        <aside class="space-y-4">
          <section class="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <h2 class="text-lg font-semibold text-slate-950">Application Status</h2>
            <div v-if="job.latest_process_type !== 'Rejected'" class="mt-4 space-y-3">
              <div class="grid grid-cols-2 gap-2">
                <label class="block">
                  <span class="text-xs font-medium text-slate-500">Date</span>
                  <input
                    v-model="statusDate"
                    type="date"
                    class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </label>
                <label class="block">
                  <span class="text-xs font-medium text-slate-500">Time</span>
                  <input
                    v-model="statusTime"
                    type="time"
                    class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </label>
              </div>

              <label class="block">
                <span class="text-xs font-medium text-slate-500">Status</span>
                <select
                  v-model="selectedStatus"
                  class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="">Add status</option>
                  <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
                </select>
              </label>

              <label v-if="isInterviewStatus(selectedStatus)" class="block">
                <span class="text-xs font-medium text-slate-500">Interview notes</span>
                <textarea
                  v-model="statusNotes"
                  rows="3"
                  placeholder="Prep notes, interviewer names, follow-up items..."
                  class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                ></textarea>
              </label>

              <button
                @click="updateStatus"
                :disabled="!selectedStatus"
                class="w-full rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Add Status
              </button>
            </div>

            <ul class="mt-4 space-y-3">
              <li
                v-for="process in processSteps"
                :key="process.id"
                class="rounded-md border border-slate-200 bg-slate-50 p-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <span class="text-sm font-semibold text-slate-900">{{ process.process_type }}</span>
                  <span class="shrink-0 text-xs text-slate-500">{{ formatProcessDateTime(process) }}</span>
                </div>
                <textarea
                  v-if="isInterviewStatus(process.process_type)"
                  v-model="process.notes"
                  rows="2"
                  placeholder="Interview notes..."
                  class="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                ></textarea>
              </li>
            </ul>
          </section>

          <section class="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <h2 class="text-lg font-semibold text-slate-950">General Notes</h2>
            <textarea
              v-model="generalNotes"
              rows="9"
              placeholder="Overall thoughts, compensation notes, recruiter context, follow-up reminders..."
              class="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            ></textarea>
          </section>
        </aside>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import DOMPurify from "dompurify";
import api from "@/services/api";
import CancelReturn from "@/components/CancelReturn.vue";
import type { UserJob, ProcessStep } from "@/types";
import { toLocalDateInputValue } from "@/utils/date";

interface LocalProcessStep extends ProcessStep {
  process_time?: string;
  notes?: string;
}

const INTERVIEW_STATUSES = [
  "Recruiter Screen",
  "Phone Screen",
  "Hiring Manager Screen",
  "Interview",
  "First Interview",
  "Second Interview",
  "Hiring Manager Interview",
  "Team Interview",
  "Panel Interview",
  "Final Interview",
  "Technical Interview",
  "Technical Assessment",
  "Case Study",
  "Portfolio Review",
  "Assessment",
];

export default {
  components: {
    CancelReturn,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const job = ref<Partial<UserJob>>({});
    const statuses = ref<string[]>([]);
    const processSteps = ref<LocalProcessStep[]>([]);
    const statusDate = ref<string>(toLocalDateInputValue());
    const statusTime = ref<string>("");
    const selectedStatus = ref<string>("");
    const statusNotes = ref<string>("");
    const generalNotes = ref<string>("");

    const domainName = computed<string>(() => {
      if (job.value.job_url) {
        try {
          const url = new URL(job.value.job_url);
          return url.hostname;
        } catch {
          console.error("Invalid URL:", job.value.job_url);
          return job.value.job_url;
        }
      }
      return "";
    });

    const fetchJobDetails = async (): Promise<void> => {
      const response = await api.fetchJobDetails(route.params.id as string);
      job.value = response.data;
      processSteps.value = (response.data.process_steps || []).map((step: ProcessStep) => ({
        ...step,
        process_time: "",
        notes: "",
      }));
    };

    const processedDescription = computed<string>(() => {
      if (!job.value.description) return "";
      const withBreaks = job.value.description.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(withBreaks);
    });

    const fetchStatuses = async (): Promise<void> => {
      const response = await api.fetchStatuses();
      statuses.value = response.data.statuses;
    };

    const isInterviewStatus = (status?: string): boolean => {
      return !!status && INTERVIEW_STATUSES.includes(status);
    };

    const formatProcessDateTime = (process: LocalProcessStep): string => {
      return [process.process_date, process.process_time].filter(Boolean).join(" ");
    };

    const updateStatus = async (): Promise<void> => {
      if (!selectedStatus.value) return;

      await api.updateJobStatus(route.params.id as unknown as number, selectedStatus.value, statusDate.value);
      const newProcessStep: LocalProcessStep = {
        id: Date.now(),
        process_type: selectedStatus.value,
        process_date: statusDate.value,
        process_time: statusTime.value,
        notes: isInterviewStatus(selectedStatus.value) ? statusNotes.value : "",
      };
      processSteps.value.unshift(newProcessStep);
      job.value.latest_process_type = selectedStatus.value;
      selectedStatus.value = "";
      statusNotes.value = "";
      statusTime.value = "";
    };

    onMounted(() => {
      fetchJobDetails();
      fetchStatuses();
    });

    return {
      job,
      statuses,
      processSteps,
      selectedStatus,
      updateStatus,
      statusDate,
      statusTime,
      statusNotes,
      generalNotes,
      domainName,
      cancel: () => router.push("/"),
      processedDescription,
      isInterviewStatus,
      formatProcessDateTime,
    };
  },
};
</script>
